import { useEffect, useState } from "react";

import useGetSubscriptionOffers from "~/clientState/hooks/useGetSubscriptionOffers";
import useGetUserQuery from "~/clientState/hooks/useGetUserQuery";
import { usePrimePriceSelectedInResults } from "~/clientState/hooks/usePrimePriceSelectedInResults";
import useShoppingBundleQuery from "~/clientState/hooks/useShoppingBundleQuery";
import useMembershipProductQuery from "~/clientState/hooks/useMembershipProductQuery";
import { getPerksForFunnel } from "~/clientState/hooks/utils/MembershipPerksUtils";
import type { PrimeUserData } from "~/clientState/providers/acquisition/PrimeUserData";
import { OnlineFunnel } from "~/constants/Funnel";
import type {
  Buyer,
  Money,
  ShoppingTraveller,
  SubscriptionOfferPeriod,
  SubscriptionResponse,
} from "~/generated/graphql";
import usePricebreakDownTotalPriceQuery from "./usePricebreakDownTotalPriceQuery";

export interface SubscriptionInfoBundle {
  primeUserData: PrimeUserData;
  perks: Money;
  offers: Array<SubscriptionResponse>;
  wasPrimePriceSelected: boolean;
  isUserLogged: boolean;
  travellers: Array<ShoppingTraveller>;
  error: unknown;
  bookingPrice: Money;
  loading: boolean;
}

export interface useSubscriptionInfoBundleProps {
  funnel?: OnlineFunnel;
  offerPeriod?: SubscriptionOfferPeriod;
}

const primeUserDataInitialState: PrimeUserData = {
  name: "",
  lastNames: "",
  isEmailRegistered: false,
  email: "",
};

export const getBuyerLastNames = (buyer: Buyer): string =>
  buyer?.lastNames || "";

const defaultMoneyObject: Money = { amount: 0, currency: "EUR" };

export const useSubscriptionInfoBundle = ({
  funnel = OnlineFunnel.FLIGHTS,
  offerPeriod,
}: useSubscriptionInfoBundleProps = {}): SubscriptionInfoBundle => {
  const { offers, error: offersErrors } = useGetSubscriptionOffers({
    offerPeriod,
  });
  const { data: userData } = useGetUserQuery();
  const {
    data: sBundleData,
    errors: sBundleErrors,
    loading: sBundleLoading,
  } = useShoppingBundleQuery();
  const wasPrimePriceSelected = usePrimePriceSelectedInResults();
  const { data: totalPriceBreakdown } = usePricebreakDownTotalPriceQuery();
  const { data: membershipProductData } = useMembershipProductQuery();
  const [error, setError] = useState<unknown>(null);
  const [isUserLogged, setIsUserLogged] = useState(false);
  const [membershipFee, setMembershipFee] = useState<Money>(defaultMoneyObject);
  const [primeUserData, setPrimeUserData] = useState<PrimeUserData>(
    primeUserDataInitialState,
  );
  const [bookingPrice, setBookingPrice] = useState<Money>(defaultMoneyObject);
  const [travellers, setTravellers] = useState<Array<ShoppingTraveller>>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setError(offersErrors);
  }, [offersErrors]);

  useEffect(() => {
    if (!sBundleLoading) {
      if (sBundleErrors && sBundleErrors.length > 0) {
        return setError(sBundleErrors[0]);
      }
      const itineraryDiscount = getPerksForFunnel(
        sBundleData?.getShoppingBundle?.membershipPerks,
        funnel,
      );
      const buyer = sBundleData?.getShoppingBundle.buyer;
      setMembershipFee({
        ...itineraryDiscount,
        amount: Math.abs(itineraryDiscount.amount),
      });
      const { name: primeMemberName, lastNames: primeMemberLastNames } =
        membershipProductData?.membershipProduct?.primeMember || {};
      if (primeMemberName && primeMemberLastNames && buyer) {
        setPrimeUserData({
          name: primeMemberName,
          lastNames: primeMemberLastNames,
          email: buyer.mail,
          isEmailRegistered: !!buyer.userRegistered,
        });
      } else if (buyer) {
        setPrimeUserData({
          name: buyer.name,
          lastNames: buyer.lastNames || "",
          email: buyer.mail,
          isEmailRegistered: !!buyer.userRegistered,
        });
      }
      const travellers = sBundleData?.getShoppingBundle.travellers;
      if (travellers) {
        setTravellers(travellers as Array<ShoppingTraveller>);
      }
      setBookingPrice(
        (prev) =>
          totalPriceBreakdown?.getCartPriceBreakDown.priceSummary.totalPrice ||
          prev,
      );
      setLoading(false);
    }
    //TODO: to be fixed by OPTIMUS
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sBundleData?.getShoppingBundle,
    sBundleLoading,
    sBundleErrors,
    setMembershipFee,
    setTravellers,
    totalPriceBreakdown,
  ]);

  useEffect(() => {
    if (userData?.getUser?.email) {
      setIsUserLogged(true);
    }
  }, [userData]);

  return {
    primeUserData,
    wasPrimePriceSelected,
    offers,
    perks: membershipFee,
    isUserLogged,
    travellers,
    error,
    bookingPrice,
    loading,
  };
};
